"use client"

import { useState } from "react"

import { cn } from "@/common/lib/utils"
import type { Gap, SkillLevel } from "@/modules/lernpfad/engine"
import type { Translator } from "@/modules/lernpfad/i18n/ui"
import { REASON_VAR, STATUS_VAR } from "./palette"

/**
 * Current → required level per skill, as a dumbbell.
 *
 * Form choice: the data's job is the DISTANCE between two points on one ordinal
 * scale, per category. A dumbbell shows that distance directly; a grouped bar
 * pair makes the reader subtract, and a single "gap size" bar throws away where
 * on the scale the person actually stands — which is what makes "advanced →
 * expert" different from "none → working" for the same gap of one step.
 *
 * One axis: the 0–4 proficiency scale, shared by both ends.
 */

interface GapDumbbellChartProps {
    gaps: Gap[]
    t: Translator
    skillLabel: (skillId: string, fallback: string) => string
    levelLabel: (level: SkillLevel) => string
    originLabel: (origin: Gap["origin"]) => string
    max?: number
}

const LEVELS: SkillLevel[] = [0, 1, 2, 3, 4]

/** Regulatory gaps borrow the mandatory hue; everything else the gap hue. */
function gapColor(origin: Gap["origin"]): string {
    if (origin === "regulatorik") return REASON_VAR.pflicht
    if (origin === "ziel") return REASON_VAR.ziel
    return REASON_VAR.luecke
}

export default function GapDumbbellChart({
    gaps,
    t,
    skillLabel,
    levelLabel,
    originLabel,
    max = 8,
}: GapDumbbellChartProps) {
    const [hover, setHover] = useState<string | null>(null)
    const shown = gaps.slice(0, max)
    if (shown.length === 0) return null

    return (
        <figure className="m-0">
            {/* Scale header — labelled once, so no number sits on every mark. */}
            <div className="mb-2 flex items-center gap-3 pl-[42%] text-[10px] text-muted-foreground">
                {LEVELS.map((level) => (
                    <span key={level} className="flex-1 text-center">
                        {levelLabel(level)}
                    </span>
                ))}
            </div>

            <div className="space-y-1">
                {shown.map((gap) => {
                    const color = gapColor(gap.origin)
                    const from = (gap.current / 4) * 100
                    const to = (gap.required / 4) * 100
                    const isHovered = hover === gap.skillId

                    return (
                        <div
                            key={gap.skillId}
                            className={cn(
                                "relative flex items-center gap-3 rounded-md px-1 py-1.5 transition-colors",
                                isHovered && "bg-muted/60",
                            )}
                            onMouseEnter={() => setHover(gap.skillId)}
                            onMouseLeave={() => setHover(null)}
                        >
                            <span className="w-[40%] shrink-0 truncate text-xs" title={gap.skillLabel}>
                                {skillLabel(gap.skillId, gap.skillLabel)}
                            </span>

                            <div className="relative h-4 flex-1">
                                {/* Scale ticks — recessive. */}
                                {LEVELS.map((level) => (
                                    <span
                                        key={level}
                                        className="absolute top-1/2 size-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                                        style={{ left: `${(level / 4) * 100}%`, background: STATUS_VAR.grid }}
                                    />
                                ))}

                                {/* The gap itself */}
                                <span
                                    className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full"
                                    style={{ left: `${from}%`, width: `${to - from}%`, background: color }}
                                />
                                {/* Current — hollow, with a surface ring so it reads over the bar */}
                                <span
                                    className="absolute top-1/2 size-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 bg-card"
                                    style={{ left: `${from}%`, borderColor: color }}
                                />
                                {/* Required — filled */}
                                <span
                                    className="absolute top-1/2 size-[9px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card"
                                    style={{ left: `${to}%`, background: color }}
                                />
                            </div>

                            <span className="w-[86px] shrink-0 text-right text-[10px] text-muted-foreground">
                                {originLabel(gap.origin)}
                            </span>
                        </div>
                    )
                })}
            </div>

            <figcaption className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                    <span className="size-[9px] rounded-full border-2 border-muted-foreground bg-card" />
                    {t("employee.col.current")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="size-[9px] rounded-full bg-muted-foreground" />
                    {t("employee.col.required")}
                </span>
                {gaps.length > max && (
                    <span>
                        +{gaps.length - max} {t("employee.gapsOpen", { count: gaps.length - max })}
                    </span>
                )}
            </figcaption>
        </figure>
    )
}
